import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ConfigProvider, App as AntdApp } from 'antd';
import faIR from 'antd/locale/fa_IR';
import dayjs from 'dayjs';
import 'dayjs/locale/fa';

import { buildAntdTheme } from '../theme/antdTheme';
import { applyBrandCssVariables } from '../theme/brandColors';
import { APP_CONFIG, STORAGE_KEYS } from '../config/app.config';
import { renderFormRequiredMark } from '../utils/formRequiredMark';

dayjs.locale('fa');

const panelAntdLocale = {
  ...faIR,
  Empty: {
    ...(faIR.Empty || {}),
    description: 'هنوز موردی در این فهرست نیست.',
  },
  Table: {
    ...(faIR.Table || {}),
    emptyText: 'هنوز موردی در این فهرست نیست.',
  },
};

const ThemeContext = createContext(null);

const getInitialTheme = () => {
  if (typeof window === 'undefined') return APP_CONFIG.defaultTheme;
  const stored = window.localStorage.getItem(STORAGE_KEYS.THEME);
  if (stored === 'light' || stored === 'dark') return stored;
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : APP_CONFIG.defaultTheme;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialTheme);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.setAttribute('dir', APP_CONFIG.direction);
    document.documentElement.setAttribute('lang', APP_CONFIG.defaultLocale);
    document.documentElement.style.colorScheme = mode === 'dark' ? 'dark' : 'light';
    applyBrandCssVariables(mode);
    // force scrollbar repaint after CSS variable swap
    void document.documentElement.offsetHeight;
  }, [mode]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEYS.THEME, mode);
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const setTheme = useCallback((next) => {
    if (next === 'light' || next === 'dark') setMode(next);
  }, []);

  const antdThemeConfig = useMemo(() => buildAntdTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      isDark: mode === 'dark',
      toggleTheme,
      setTheme,
    }),
    [mode, toggleTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        direction={APP_CONFIG.direction}
        locale={panelAntdLocale}
        theme={antdThemeConfig}
        componentSize="middle"
        form={{ requiredMark: renderFormRequiredMark }}
      >
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
};
